import { ModalDialog } from '@edx/paragon';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import messages from '../../forms/elements/FormControls.messages';
import { hasSkills, maxCategoryOptions, maxSkillsOptions } from '../constants';
import profileMessages from '../../ProfilePage.messages';

const CustomTagModal = ({ isOpen, onClose, title, categories, userCategories, onSave }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const { formatMessage } = useIntl();

  const withSkills = hasSkills(categories);

  useEffect(() => {
    setSelectedCategories(userCategories || []);
  }, [userCategories]);

  const isActiveCategory = id => selectedCategories.some(category => category.id === id);

  const handleTagClick = category => {
    setSelectedCategories(prevSelected => {
      const isExist = prevSelected.some(cat => cat.id === category.id);

      if (isExist) {
        return prevSelected.filter(cat => cat.id !== category.id);
      } else {
        // Check selection limits
        if (withSkills) {
          // For skills: max 2 per subcategory
          const categoryId =
            category.category?.[0]?.id ||
            categories.find(cat => cat.skills?.some(skill => skill.id === category.id))?.id;

          const skillsInSameCategory = prevSelected.filter(skill => {
            const skillCategoryId =
              skill.category?.[0]?.id ||
              categories.find(cat => cat.skills?.some(s => s.id === skill.id))?.id;
            return skillCategoryId === categoryId;
          });

          if (skillsInSameCategory.length >= maxSkillsOptions) {
            return prevSelected;
          }
        } else {
          // For simple categories: max 3 total
          if (prevSelected.length >= maxCategoryOptions) {
            return prevSelected;
          }
        }

        return [...prevSelected, category];
      }
    });
  };

  const handleSave = () => {
    if (onSave) {
      onSave(selectedCategories);
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedCategories(userCategories || []);
    onClose();
  };

  const renderCategoriesWithSkills = () => {
    return categories.map(category => (
      <div key={category.id} className="tw-mb-10 md:tw-mb-12">
        <p className="tw-text-lg tw-font-medium !tw-mb-4 md:tw-text-[22px] md:!tw-mb-6">
          {category.name}
        </p>
        <div className="tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-3">
          {category.skills?.map(skill => (
            <button
              key={skill.id}
              onClick={() => handleTagClick(skill)}
              className={`${isActiveCategory(skill.id) ? 'tag-active' : 'tag'} tw-text-center tw-cursor-pointer`}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleTagClick(skill);
                }
              }}
            >
              {skill.name}
            </button>
          ))}
        </div>
      </div>
    ));
  };

  const renderSimpleCategories = () => {
    return (
      <div className="tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-3 tw-justify-center">
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => handleTagClick(category)}
            className={`${isActiveCategory(category.id) ? 'tag-active' : 'tag'} tw-text-center tw-cursor-pointer`}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTagClick(category);
              }
            }}
          >
            {category.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <ModalDialog
      title="Custom dialog"
      isOpen={isOpen}
      onClose={handleClose}
      hasCloseButton={false}
      className="tw-max-w-none tw-rounded-t-[32px] tw-px-4 tw-pt-6 tw-pb-4 tw-mx-0 md:tw-max-w-[725px] md:tw-rounded-[32px] md:tw-px-12 md:tw-pt-10"
    >
      <ModalDialog.Header className="!tw-p-0 tw-min-h-0 tw-mb-8 md:tw-mb-12">
        <ModalDialog.Title className="!tw-text-center tw-mr-0 tw-font-medium !tw-text-[20px] md:!tw-text-[24px]">
          {title === formatMessage(profileMessages.userSkillsTitle)
            ? formatMessage(profileMessages.userSkillsModalTitle)
            : title}
          <p className="tw-mt-2 tw-font-normal tw-text-[16px] !tw-text-center">
            {withSkills
              ? formatMessage(messages['profile.formcontrols.skills.subtitle'])
              : formatMessage(messages['profile.formcontrols.categories.subtitle'])}
          </p>
        </ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body className="!tw-p-0">
        {withSkills ? renderCategoriesWithSkills() : renderSimpleCategories()}
      </ModalDialog.Body>

      <ModalDialog.Footer className="tw-p-0 tw-flex-col-reverse tw-justify-between tw-gap-2 md:tw-flex-row md:tw-gap-4">
        <button onClick={handleClose} className="button-tertiary tw-w-full tw-font-medium">
          {formatMessage(messages['profile.formcontrols.button.cancel'])}
        </button>
        <button onClick={handleSave} className="button-secondary tw-w-full tw-font-medium">
          {formatMessage(messages['profile.formcontrols.button.done'])}
        </button>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

CustomTagModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  title: PropTypes.string,
  categories: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        is_active: PropTypes.bool,
      }),
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        is_active: PropTypes.bool,
        skills: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            is_active: PropTypes.bool,
            category: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                is_active: PropTypes.bool,
              }),
            ),
          }),
        ),
      }),
    ),
  ]).isRequired,
  userCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      is_active: PropTypes.bool,
    }),
  ),
};

CustomTagModal.defaultProps = {
  isOpen: false,
  onSave: null,
  title: '',
  userCategories: [],
};

export default CustomTagModal;
